/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * What the Analytics sections share (the usage analytics contract, section 5): their props (the lens and its query), a
 * failed load in words with Try again, the small area trend the kit has no shape for (tokens only, drawn with classes from
 * kit.css), a bar list of named counts on the kit's MeterRow, and the CSV download.
 */
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { errorText } from '../../lib/errors';
import { statusOf } from '../../lib/fleetHttp';
import { dayLabel, nameLabel, type NamedCount, type UsageLens, type UsageQuery } from '../../lib/usage';
import { EmptyState, ErrorState, MeterRow } from '../../ui';
import { count } from '../../ui/format';

export interface SectionProps {
  lens: UsageLens;
  /** The lens as every read takes it (`window`, `surface`, `audience`, `hide_admin`). */
  query: UsageQuery;
  /** The query serialized: the load key every section reloads on. */
  lensKey: string;
}

/** A failed read or write in words: the dark service, a refused write, storage that is down, else the service's own words. */
export function usageFailure(error: unknown, what = 'The usage data could not be read.'): string {
  const status = statusOf(error);
  if (status === 404) return 'The usage analytics routes are not on this service yet: the page reads empty until the service release that carries them.';
  if (status === 401) return 'Your sign-in has expired. Sign in again.';
  if (status === 403) return 'Only IT admins can make this change.';
  if (status === 503) return 'Usage storage is unavailable just now. Try again in 30 seconds.';
  return errorText(error, what);
}

/** A failed load, in words, with its status and Try again; it replaces only the section that failed. */
export function UsageError({ title, error, onRetry, retrying }: { title: ReactNode; error: unknown; onRetry?: () => void; retrying?: boolean }) {
  const said = new ApiError(usageFailure(error), statusOf(error));
  return <ErrorState compact title={title} error={said} onRetry={onRetry} retrying={retrying} />;
}

const TREND_W = 600;
const TREND_H = 120;
const PAD = 4;

/**
 * A small area trend (the Overview's daily trend, a feature's days): pure SVG, the fill and the line coloured by kit.css
 * classes on the data token, no inline style. The first and last day label the x axis under it when days are given.
 */
export function Trend({ values, label, days }: { values: readonly number[]; label: string; days?: readonly string[] }) {
  if (!values.length) return <EmptyState compact title="No days yet">The trend fills in as days are collected.</EmptyState>;
  const top = Math.max(1, ...values.map((value) => (Number.isFinite(value) ? value : 0)));
  const step = values.length > 1 ? (TREND_W - PAD * 2) / (values.length - 1) : 0;
  const points = values.map((value, index) => {
    const x = values.length > 1 ? PAD + index * step : TREND_W / 2;
    const y = TREND_H - PAD - (Math.max(0, Number.isFinite(value) ? value : 0) / top) * (TREND_H - PAD * 2);
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10] as const;
  });
  const line = points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x} ${y}`).join(' ');
  const area = `${line} L${points[points.length - 1]![0]} ${TREND_H - PAD} L${points[0]![0]} ${TREND_H - PAD} Z`;
  const first = days?.[0];
  const last = days?.[days.length - 1];
  return (
    <div className="usage-trend-box">
      <svg className="usage-trend" role="img" aria-label={`${label}: ${values.map((value) => count(value)).join(', ')}`} viewBox={`0 0 ${TREND_W} ${TREND_H}`} preserveAspectRatio="none">
        <path className="usage-trend-area" d={area} />
        <path className="usage-trend-line" d={line} vectorEffect="non-scaling-stroke" />
      </svg>
      {first && last ? <div className="usage-trend-axis"><span>{dayLabel(first)}</span><span>{dayLabel(last)}</span></div> : null}
    </div>
  );
}

/** Named counts as bars against the largest (entry screens, a focus screen's neighbours, top screens now). */
export function NamedBars({ rows, label, limit = 10, empty, onPick }: { rows: readonly NamedCount[]; label: string; limit?: number; empty: ReactNode; onPick?: (name: string) => void }) {
  if (!rows.length) return <>{empty}</>;
  const top = Math.max(1, ...rows.map((row) => row.count));
  return (
    <div className="usage-bars">
      {rows.slice(0, limit).map((row) => (
        <MeterRow
          key={row.name}
          label={onPick ? <button type="button" className="usage-link" onClick={() => onPick(row.name)}>{nameLabel(row.name)}</button> : nameLabel(row.name)}
          figure={count(row.count)}
          value={row.count / top}
          meterLabel={`${label}: ${nameLabel(row.name)}, ${count(row.count)}`}
        />
      ))}
    </div>
  );
}

/** Hands the browser a file to save (the CSV export); nothing happens where the page has no object URLs (a test). */
export function downloadText(fileName: string, text: string, type = 'text/csv;charset=utf-8'): boolean {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || typeof document === 'undefined') return false;
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return true;
}
